import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Stack, Box } from '@mui/system';
import { useLocation, useNavigate } from 'react-router-dom';
import _ from 'lodash';

// Redux
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import {
	getActiveStep,
	getSteps,
	getDigitalSteps,
	isLoading,
	setActiveStep,
	getAddBalanceState,
} from 'state/reducers/balance.reducers';
import {
	cancelDialogProps,
	getHistory,
	getMobile,
	getUi,
	ifPageChange,
	setCheckBeforeNavigate,
	togglePage,
} from 'state/reducers/ui.reducers';
import { selectTranslations } from 'state/reducers/translation.reducers';

// Services
import {
	cancelAddBalanceProcess,
	getSuggestedAltshulerAccountByCompanyId,
	summarizeBalanceProcess,
} from 'services/balance.services';
import { getAltshulerAccountsByCompanyId, getBankAccountsByCompanyId } from 'services/bank-account.services';

// Constants
import {
	BalanceDepositType,
	balanceSteps,
	balanceTransStepsPrefix,
	digitalBalanceAddStepsPrefix,
} from 'constants/balance.constants';
import { pagesEnum } from 'constants/ui.constants';

// Components
import CancelDialog from 'components/CancelDialog';
import WaitingMessage from 'components/WaitingMessage';
import MobileNavbar from 'sections/layout/MobileNavbar';
import { CenteredNarrowContent } from 'theme/elements/Content';
import Stepping from 'theme/elements/Stepper';

// Shared Components
import BalanceAddHeader from './components/BalanceAddHeader';

// Steps
import Step1 from './steps/step1';
import Step2 from './steps/step2';

import ManualStep3 from '../manual/steps/step3';
import DigitalStep3 from '../digital/steps/step3';
import ManualStep4 from '../manual/steps/step4';

// Utils
import { getUrlBack } from 'theme/elements/Headers';
import { translatedCancelPopup } from 'utils';

interface Props {
	handleClose?: () => void;
}

export default function AddBalanceProcess({ handleClose }: Props) {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const t = useAppSelector(selectTranslations);
	const isMobile = useAppSelector(getMobile);
	const activeStep = useAppSelector(getActiveStep);
	const balanceState = useAppSelector(getAddBalanceState);
	const ui = useAppSelector(getUi);
	const isLeavingPage = useAppSelector(ifPageChange);
	const history = useAppSelector(getHistory);
	const dialogProps = useAppSelector(cancelDialogProps);
	const loading = useAppSelector(isLoading);

	// Dynamic selectors based on process type
	const totalSteps = useAppSelector(balanceState === BalanceDepositType.Digital ? getDigitalSteps : getSteps);
	const stepsPrefix =
		balanceState === BalanceDepositType.Digital ? digitalBalanceAddStepsPrefix : balanceTransStepsPrefix;

	const [openCancelDialog, setOpenCancelDialog] = useState(false);
	const [file, setFile] = useState<File | null>(null);

	const urlback = getUrlBack(location.pathname, history[history.length - 1]);
	const dialogPropsTranslated = useMemo(() => translatedCancelPopup(dialogProps), [dialogProps]);

	const mutualProps = {
		handleBack: (step: balanceSteps) => dispatch(setActiveStep(step)),
		handleCancel: () => setOpenCancelDialog(true),
	};

	const handleFileSave = (file: File | null) => {
		setFile(file);
	};

	// Dynamic navigation logic based on process type
	const getNavigationFlags = () => {
		if (balanceState === BalanceDepositType.Digital) {
			return {
				checkBeforeNavigate: activeStep > 0 && activeStep < 3,
				noCancelSteps: [0, 2],
			};
		} else {
			return {
				checkBeforeNavigate: activeStep < 3,
				noCancelSteps: [0, 3],
			};
		}
	};

	const { checkBeforeNavigate, noCancelSteps } = getNavigationFlags();

	useEffect(() => {
		dispatch(setCheckBeforeNavigate(checkBeforeNavigate));
		window.scrollTo(0, 0);
	}, [activeStep, checkBeforeNavigate]);

	useEffect(() => {
		if (isLeavingPage && ui.checkBeforeNavigate) {
			setOpenCancelDialog(true);
		}
	}, [isLeavingPage]);

	useEffect(() => {
		// Common API calls
		dispatch(getAltshulerAccountsByCompanyId());
		dispatch(getSuggestedAltshulerAccountByCompanyId());

		// Digital-specific API call
		if (balanceState === BalanceDepositType.Digital) {
			dispatch(getBankAccountsByCompanyId());
		}
	}, [balanceState]);

	const handleMobileBack = () => {
		if (activeStep > 0 && activeStep < (balanceState === BalanceDepositType.Digital ? 3 : 3)) {
			dispatch(setActiveStep(_.values(balanceSteps)[activeStep - 1]));
		} else {
			dispatch(summarizeBalanceProcess());
			if (balanceState === 'manual' && handleClose) {
				handleClose();
			} else {
				dispatch(togglePage(urlback));
			}
		}
	};

	const handleCancelConfirm = () => {
		setOpenCancelDialog(false);
		dispatch(cancelAddBalanceProcess());

		if (isLeavingPage) {
			navigate(ui.to);
		} else if (balanceState === 'manual' && handleClose) {
			handleClose();
		} else {
			navigate(`/${pagesEnum.BALANCE}`);
		}
	};

	const renderStepContent = () => {
		// Step 3 for digital has special layout
		if (activeStep === 2 && balanceState === BalanceDepositType.Digital) {
			return (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						minHeight: '60vh',
						width: '100%',
					}}
				>
					<Box sx={{ maxWidth: '900px', width: '100%' }}>
						<DigitalStep3 transferProofFile={file} {...mutualProps} />
					</Box>
				</Box>
			);
		}

		// All other steps use CenteredNarrowContent
		return (
			<CenteredNarrowContent>
				<Fragment>
					{activeStep === 0 && <Step1 {...mutualProps} />}
					{activeStep === 1 && <Step2 handleTransferProofFile={handleFileSave} {...mutualProps} />}
					{activeStep === 2 && balanceState === 'manual' && (
						<ManualStep3 transferProofFile={file} {...mutualProps} />
					)}
					{activeStep === 3 && balanceState === 'manual' && (
						<ManualStep4 {...mutualProps} handleClose={handleClose!} />
					)}
				</Fragment>
			</CenteredNarrowContent>
		);
	};

	if (loading) {
		return <WaitingMessage />;
	}

	return (
		<Fragment>
			<MobileNavbar
				handleBack={handleMobileBack}
				noCancel={_.includes(noCancelSteps, activeStep)}
				handleCancel={() => setOpenCancelDialog(true)}
				cancelLabel={t(`page.balance.add.step.${activeStep + 1}.cancel_button.text`)}
			/>
			<Stack spacing={{ sm: 5, xs: 1 }}>
				{!isMobile && (
					<BalanceAddHeader
						title={t(
							balanceState === BalanceDepositType.Digital
								? 'page.balance.add.digital.header.title'
								: 'page.balance.add.header.title'
						)}
						text={t(
							balanceState === BalanceDepositType.Digital
								? 'page.balance.add.digital.header.text'
								: 'page.balance.add.header.text'
						)}
					/>
				)}
				<Stack spacing={{ sm: 5, xs: 3 }}>
					<Stepping activeStep={activeStep} total={totalSteps} stepsPrefix={stepsPrefix} />
					{renderStepContent()}
				</Stack>
				<CancelDialog
					{...dialogPropsTranslated}
					open={openCancelDialog}
					handleContinue={() => {
						setOpenCancelDialog(false);
						if (isLeavingPage) {
							dispatch(togglePage());
						}
					}}
					handleCancel={handleCancelConfirm}
				/>
			</Stack>
		</Fragment>
	);
}
